import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Inbox } from 'lucide-react';

import { fetchLeadInbox, revealLeadContact } from '@/dashboard/api';
import { useDashboardMeContext } from '@/dashboard/me-context';
import { useClinicLens } from '@/dashboard/clinic-context';
import ClinicScopeLabel from '@/dashboard/ui/ClinicScopeLabel';
import { formatFollowupAge } from '@/dashboard/clinical-workbench/CocFollowupCockpit';
import LeadContactRevealControl from '@/dashboard/owner/LeadContactReveal';
import {
  LEAD_INBOX_DARK_SOURCE_COPY,
  LEAD_INBOX_DEFAULT_FILTERS,
  LEAD_INBOX_EMPTY_HINT,
  LEAD_INBOX_EMPTY_TITLE,
  LEAD_INBOX_HINT,
  LEAD_INBOX_NO_NAME_LABEL,
  LEAD_INBOX_ORG_INTAKE_HREF,
  LEAD_INBOX_RESTRICTED_COPY,
  LEAD_INBOX_SCAN_CAPPED_NOTE,
  LEAD_INBOX_TITLE,
  LEAD_INBOX_UNAVAILABLE_COPY,
  isLeadInboxAccessRestricted,
  leadInboxDetailHref,
  leadInboxQueryParams,
  matchedCountLabel,
  type LeadInboxFilters,
  type LeadInboxResult,
  type LeadInboxRow,
} from '@/dashboard/lead-inbox/lead-inbox-view';

/**
 * AdoraCare -- all-source lead inbox page (ALL-SOURCE-LEAD-INBOX-V1, B3 /
 * Story 2.2). LIST-FIRST, mobile-friendly (the org-intake / #183
 * CocFollowupCockpit row idiom: flex-wrap rows, no table).
 *
 * COMPOSED from existing pieces: the org-intake row/chip idiom, the
 * EXISTING audited LeadContactRevealControl (pure reuse -- flags/data
 * missing renders its own honest states), links to the EXISTING Lead 360
 * detail route (/dashboard/leads/:id -- offered ONLY when the server says
 * the row's clinic is in the caller's scope AND me.leadsWorkspaceEnabled;
 * no dead links), and clinic-pending rows linking into the EXISTING
 * org-intake set-clinic flow. NO new CRM surface, NO new Lead 360, NO
 * write of any kind (this page is read-only against the lead engine).
 *
 * LABELS: every source / stage / status / aging / filter-option string is
 * rendered VERBATIM from the wire (the backend's ONE display-label
 * helper). This page maps no vocabulary token to copy.
 *
 * FILTERS: exactly five (clinic state / source / needs action / clinic /
 * aging -- locked design item 4). Changing any filter resets pagination.
 * Empty states are honest, including the DARK-SOURCE copy while the
 * WhatsApp / webchat capture flags are off. NO sample rows.
 */

// ---- Presentational state ----

export type LeadInboxState =
  | { kind: 'loading' }
  | { kind: 'error'; restricted: boolean }
  | { kind: 'ready'; data: LeadInboxResult };

/** Honest read-failure states -- NEVER a fabricated empty inbox. */
export const LEAD_INBOX_ERROR_STATE: LeadInboxState = {
  kind: 'error',
  restricted: false,
};
export const LEAD_INBOX_RESTRICTED_STATE: LeadInboxState = {
  kind: 'error',
  restricted: true,
};

// ---- Filter bar (options ride the wire; chrome labels are local) ----

export interface LeadInboxFilterBarProps {
  readonly data: LeadInboxResult;
  readonly filters: LeadInboxFilters;
  readonly onChange: (next: LeadInboxFilters) => void;
}

export function LeadInboxFilterBar({
  data,
  filters,
  onChange,
}: LeadInboxFilterBarProps): JSX.Element {
  const set = (key: keyof LeadInboxFilters) => (value: string) =>
    onChange({ ...filters, [key]: value });
  return (
    <div className="lead-inbox__filters" data-testid="lead-inbox-filters">
      <label>
        Clinic state:{' '}
        <select
          data-testid="lead-inbox-filter-clinic-state"
          value={filters.clinicState}
          onChange={(e) => set('clinicState')(e.target.value)}
        >
          {data.clinicStateOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        Source:{' '}
        <select
          data-testid="lead-inbox-filter-source"
          value={filters.source}
          onChange={(e) => set('source')(e.target.value)}
        >
          <option value="all">All</option>
          {data.sourceOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        Needs action:{' '}
        <select
          data-testid="lead-inbox-filter-needs-action"
          value={filters.needsAction}
          onChange={(e) => set('needsAction')(e.target.value)}
        >
          {data.needsActionOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        Aging:{' '}
        <select
          data-testid="lead-inbox-filter-aging"
          value={filters.aging}
          onChange={(e) => set('aging')(e.target.value)}
        >
          <option value="all">All</option>
          {data.agingOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

// ---- One inbox row (compact summary; wire labels verbatim) ----

export interface LeadInboxRowViewProps {
  readonly row: LeadInboxRow;
  readonly nowMs: number;
  /** me.leadsWorkspaceEnabled -- the detail surface can render (no dead links). */
  readonly workspaceEnabled: boolean;
  /** The audited reveal control (injected so SSR tests render without state). */
  readonly revealControl?: React.ReactNode;
}

export function LeadInboxRowView({
  row,
  nowMs,
  workspaceEnabled,
  revealControl,
}: LeadInboxRowViewProps): JSX.Element {
  const detailHref = workspaceEnabled ? leadInboxDetailHref(row) : null;
  return (
    <li
      className="coc-cockpit__row lead-inbox__row"
      data-testid={`lead-inbox-row-${row.leadId}`}
    >
      <div className="coc-cockpit__row-main">
        <span
          className="coc-cockpit__name"
          data-testid={`lead-inbox-name-${row.leadId}`}
        >
          {row.name ?? LEAD_INBOX_NO_NAME_LABEL}
        </span>
        <span className="cockpit-pill cockpit-pill--muted">
          Lead #{row.leadId}
        </span>
        <span
          className="coc-cockpit__age"
          data-testid={`lead-inbox-age-${row.leadId}`}
          title={row.agingLabel ?? undefined}
        >
          {formatFollowupAge(row.createdAt, nowMs)}
        </span>
      </div>
      <div
        className="lead-inbox__chips"
        data-testid={`lead-inbox-chips-${row.leadId}`}
      >
        <span className="badge badge--info">{row.sourceLabel}</span>
        <span className="badge" data-testid={`lead-inbox-stage-${row.leadId}`}>
          {row.stageLabel}
        </span>
        {row.clinicLabel !== null ? (
          <span className="badge">{row.clinicLabel}</span>
        ) : null}
        {row.chips.map((chip) => (
          <span
            key={chip.key}
            className={
              chip.key === 'stale' || chip.key === 'unassigned'
                ? 'badge badge--danger'
                : chip.key === 'needs_action' || chip.key === 'clinic_pending'
                  ? 'badge badge--warning'
                  : chip.key === 'identity_converted'
                    ? 'badge badge--success'
                    : 'badge'
            }
            data-testid={`lead-inbox-chip-${row.leadId}-${chip.key}`}
          >
            {chip.label}
          </span>
        ))}
        <span
          className="badge badge--muted"
          data-testid={`lead-inbox-owner-${row.leadId}`}
        >
          Owner: {row.ownerLabel}
        </span>
      </div>
      {revealControl ?? null}
      <div className="coc-cockpit__row-actions">
        {row.isOrgPending ? (
          <Link
            to={LEAD_INBOX_ORG_INTAKE_HREF}
            className="ac-btn ac-btn--primary ac-btn--sm"
            data-testid={`lead-inbox-org-intake-link-${row.leadId}`}
          >
            Assign clinic in Org intake
          </Link>
        ) : detailHref !== null ? (
          <Link
            to={detailHref}
            className="ac-btn ac-btn--secondary ac-btn--sm"
            data-testid={`lead-inbox-detail-link-${row.leadId}`}
          >
            Open Lead 360
          </Link>
        ) : null}
      </div>
    </li>
  );
}

// ---- The inbox view (pure -- renderToString-testable) ----

export interface LeadInboxViewProps {
  readonly state: LeadInboxState;
  readonly filters: LeadInboxFilters;
  readonly nowMs: number;
  readonly workspaceEnabled: boolean;
  readonly onFiltersChange: (next: LeadInboxFilters) => void;
  readonly onPrevPage: () => void;
  readonly onNextPage: () => void;
  readonly renderReveal?: (row: LeadInboxRow) => React.ReactNode;
  /** The clinic this surface is showing, already a human label (never an id);
   *  null when the lens has no options resolved. */
  readonly clinicScopeLabel?: string | null;
}

export function LeadInboxView({
  state,
  filters,
  nowMs,
  workspaceEnabled,
  onFiltersChange,
  onPrevPage,
  onNextPage,
  renderReveal,
  clinicScopeLabel,
}: LeadInboxViewProps): JSX.Element {
  return (
    <section
      className="clinic-table coc-cockpit lead-inbox"
      data-testid="lead-inbox"
      aria-label={LEAD_INBOX_TITLE}
    >
      <div className="coc-cockpit__head">
        <h2 className="clinic-table__title">{LEAD_INBOX_TITLE}</h2>
        {/* Phase 3 (G1): this surface NAMES the clinic it is showing, through
            THE ONE scope primitive -- here it genuinely IS the surface's scope
            (the lens), unlike the search card's record-level branch fact. */}
        {clinicScopeLabel !== null && clinicScopeLabel !== undefined ? (
          <ClinicScopeLabel
            label={clinicScopeLabel}
            testId="lead-inbox-clinic-scope"
          />
        ) : null}
        {state.kind === 'ready' ? (
          <span className="coc-cockpit__count" data-testid="lead-inbox-count">
            {matchedCountLabel(state.data.totalMatched, state.data.scanComplete)}
          </span>
        ) : null}
      </div>
      <p className="clinic-workbench__hint" data-testid="lead-inbox-hint">
        {LEAD_INBOX_HINT}
      </p>
      {state.kind === 'ready' &&
      state.data.role === 'owner' &&
      state.data.ownerRole !== 'owner' ? (
        <p className="clinic-workbench__hint" data-testid="lead-inbox-owner-note">
          Worked by: {state.data.ownerRole} (configured in Routing settings).
        </p>
      ) : null}

      {state.kind === 'loading' ? (
        <p className="followups-empty__sub" role="status">
          Loading the lead inbox...
        </p>
      ) : state.kind === 'error' ? (
        <p
          className="followups-empty__sub"
          role="status"
          data-testid={
            state.restricted ? 'lead-inbox-restricted' : 'lead-inbox-error'
          }
        >
          {state.restricted
            ? LEAD_INBOX_RESTRICTED_COPY
            : LEAD_INBOX_UNAVAILABLE_COPY}
        </p>
      ) : (
        <>
          <LeadInboxFilterBar
            data={state.data}
            filters={filters}
            onChange={onFiltersChange}
          />
          {state.data.rows.length === 0 ? (
            <div
              className="followups-empty"
              role="status"
              data-testid="lead-inbox-empty"
            >
              <Inbox
                className="followups-empty__icon"
                size={20}
                aria-hidden="true"
              />
              <p className="followups-empty__title">{LEAD_INBOX_EMPTY_TITLE}</p>
              <p
                className="followups-empty__sub"
                data-testid={
                  state.data.darkSources.length > 0
                    ? 'lead-inbox-dark-sources'
                    : 'lead-inbox-empty-hint'
                }
              >
                {state.data.darkSources.length > 0
                  ? LEAD_INBOX_DARK_SOURCE_COPY
                  : LEAD_INBOX_EMPTY_HINT}
              </p>
            </div>
          ) : (
            <>
              <ul className="coc-cockpit__list" data-testid="lead-inbox-list">
                {state.data.rows.map((row) => (
                  <LeadInboxRowView
                    key={String(row.leadId)}
                    row={row}
                    nowMs={nowMs}
                    workspaceEnabled={workspaceEnabled}
                    revealControl={renderReveal?.(row)}
                  />
                ))}
              </ul>
              {!state.data.scanComplete ? (
                <p
                  className="clinic-workbench__note"
                  data-testid="lead-inbox-scan-capped"
                >
                  {LEAD_INBOX_SCAN_CAPPED_NOTE}
                </p>
              ) : null}
              <div className="lead-inbox__pager" data-testid="lead-inbox-pager">
                <button
                  type="button"
                  className="ac-btn ac-btn--secondary ac-btn--sm"
                  data-testid="lead-inbox-prev"
                  disabled={state.data.offset === 0}
                  onClick={onPrevPage}
                >
                  Previous
                </button>
                <span className="lead360-muted">
                  {state.data.offset + 1}-
                  {state.data.offset + state.data.rows.length} of{' '}
                  {matchedCountLabel(
                    state.data.totalMatched,
                    state.data.scanComplete,
                  )}
                </span>
                <button
                  type="button"
                  className="ac-btn ac-btn--secondary ac-btn--sm"
                  data-testid="lead-inbox-next"
                  disabled={!state.data.hasMore}
                  onClick={onNextPage}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}

// ---- Stateful wirer (default export) ----

/**
 * OWNER-FUNNEL-FOLDIN-V1 (Story 6.2, drilldown contract A3): initial
 * inbox filters from URL search params -- the owner funnel links here
 * with explicit filter params (source / clinicId / needsAction). Read
 * ONCE at mount; a no-param URL yields exactly the defaults, and the
 * view stays param-free. Unknown filter VALUES still hit the backend's
 * validation (a stable 400, never a silent empty inbox). Pure; exported
 * for tests.
 */
export function leadInboxFiltersFromSearch(
  searchParams: URLSearchParams,
): LeadInboxFilters {
  const fromUrl = (key: keyof LeadInboxFilters): string => {
    const v = searchParams.get(key);
    return v !== null && v.trim().length > 0
      ? v.trim()
      : LEAD_INBOX_DEFAULT_FILTERS[key];
  };
  return {
    clinicState: fromUrl('clinicState'),
    source: fromUrl('source'),
    needsAction: fromUrl('needsAction'),
    clinicId: fromUrl('clinicId'),
    aging: fromUrl('aging'),
  };
}

export default function LeadInboxPage(): JSX.Element {
  const meCtx = useDashboardMeContext();
  // THE LENS OWNS "WHERE AM I" (119-CLINIC-OPERATING-SPINE-V1 Phase 3, gap G1).
  // The lead inbox is a triage QUEUE -- operational work, not branch
  // comparison -- so it reads the lens and keeps no private clinic value. Its
  // own Clinic select is gone: a second control for a value the header lens
  // owns is how a surface comes to disagree with the chrome above it.
  const clinicCtx = useClinicLens();
  const lensClinicId = clinicCtx?.selectedClinicId ?? null;
  const [state, setState] = useState<LeadInboxState>({ kind: 'loading' });
  const [searchParams] = useSearchParams();
  const [otherFilters, setOtherFilters] = useState<LeadInboxFilters>(() =>
    leadInboxFiltersFromSearch(searchParams),
  );
  // ONE derived value: every other filter is this surface's own, the clinic is
  // the lens's. 'all' is the lens's "All clinics", not a private default.
  const filters: LeadInboxFilters = useMemo(
    () => ({
      ...otherFilters,
      clinicId: lensClinicId !== null ? String(lensClinicId) : 'all',
    }),
    [otherFilters, lensClinicId],
  );
  const [offset, setOffset] = useState<number>(0);
  const [nowMs, setNowMs] = useState<number>(() => Date.now());

  const load = useCallback((f: LeadInboxFilters, o: number) => {
    let cancelled = false;
    setState({ kind: 'loading' });
    setNowMs(Date.now());
    fetchLeadInbox(leadInboxQueryParams(f, o))
      .then((data) => {
        if (!cancelled) setState({ kind: 'ready', data });
      })
      .catch((err: unknown) => {
        // Honest unavailable/restricted states -- never a fabricated empty.
        if (!cancelled) {
          setState(
            isLeadInboxAccessRestricted(err)
              ? LEAD_INBOX_RESTRICTED_STATE
              : LEAD_INBOX_ERROR_STATE,
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(filters, offset), [load, filters, offset]);

  const onFiltersChange = useCallback((next: LeadInboxFilters) => {
    // The clinic is not this surface's to set -- it comes from the lens.
    setOtherFilters(next);
    setOffset(0); // a filter change always restarts pagination
  }, []);

  const onPrevPage = useCallback(() => {
    setOffset((o) =>
      state.kind === 'ready' ? Math.max(0, o - state.data.limit) : o,
    );
  }, [state]);

  const onNextPage = useCallback(() => {
    setOffset((o) => (state.kind === 'ready' ? o + state.data.limit : o));
  }, [state]);

  return (
    <LeadInboxView
      state={state}
      filters={filters}
      nowMs={nowMs}
      workspaceEnabled={meCtx?.me?.leadsWorkspaceEnabled === true}
      clinicScopeLabel={
        clinicCtx !== null && clinicCtx.selectedLabel !== ''
          ? clinicCtx.selectedLabel
          : null
      }
      onFiltersChange={onFiltersChange}
      onPrevPage={onPrevPage}
      onNextPage={onNextPage}
      renderReveal={(row) => (
        <LeadContactRevealControl
          availability={row.phoneAvailability}
          reveal={(purpose) => revealLeadContact(String(row.leadId), purpose)}
        />
      )}
    />
  );
}
